import { useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import formatter from 'Utils/formatter';
import Loading from 'Helpers/Loading';
import { ContentContainer, Title, Button } from 'Helpers/GlobalStyles';
import { TextContainer, Label, Text, Link } from './UpdatePayment.styles';
import { getFailedPaymentDetails, createCheckoutSession } from '../../services';

const UpdatePayment = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [failedPaymentDetails, setFailedPaymentDetails] = useState<
    Awaited<ReturnType<typeof getFailedPaymentDetails>> | undefined
  >(undefined);

  useEffect(() => {
    getFailedPaymentDetails().then((response) => {
      setFailedPaymentDetails(response);
      setIsLoading(false);
    });
  }, []);

  const makePayment = () => {
    const currentUrl = window.location.href
      .replace(/(\?|#).*$/, '')
      .replace(/\?.*$/, '')
      .replace(/\/$/, '');
    const successUrl = `${currentUrl}/success`;

    setIsLoading(true);

    const stripePromise = loadStripe(`${process.env.STRIPE_API_KEY}`);
    const checkoutSessionPromiese = createCheckoutSession(
      successUrl,
      currentUrl
    );

    Promise.all([stripePromise, checkoutSessionPromiese])
      .then(([stripe, checkoutSession]) =>
        stripe?.redirectToCheckout({ sessionId: checkoutSession.id })
      )
      .catch(() => setIsLoading(false));
  };

  if (isLoading) return <Loading />;

  if (!failedPaymentDetails)
    return (
      <ContentContainer>
        <Title>Something Went Wrong</Title>
        <Text>There was a problem finding your payment details</Text>
      </ContentContainer>
    );

  if (failedPaymentDetails?.hasFailedPayment && failedPaymentDetails.isResolved)
    return <Redirect to="/make-payment/success" />;

  if (!failedPaymentDetails.hasFailedPayment)
    return (
      <ContentContainer>
        <Title>No Payment Found</Title>
        <Text>We could not find your payment details</Text>
      </ContentContainer>
    );

  if (failedPaymentDetails.isExpired)
    return (
      <ContentContainer>
        <Title>Link Expired</Title>
        <Text>
          Please contact{' '}
          <Link href="mailto:recovery@carvana.com" target="_blank">
            recovery@carvana.com
          </Link>{' '}
          and our Payment Resolution team can assist you
        </Text>
      </ContentContainer>
    );

  return (
    <ContentContainer>
      <Title>Make a Payment</Title>
      <TextContainer>
        <Label>Payment Amount</Label>
        <Text>{formatter.dollarAmount(failedPaymentDetails.amount)}</Text>
      </TextContainer>
      <Button onClick={() => makePayment()}>Enter Card Info</Button>
    </ContentContainer>
  );
};

export default UpdatePayment;
